import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Lightbulb, X } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useDialerStore } from '../../stores/dialerStore';
import { useTourStore } from '../../stores/tourStore';
import { useUiStore } from '../../stores/uiStore';
import { PRIVILEGED_ROLES } from '../../types';
import { CHAPTERS, buildSteps, type TourStep } from './tourSteps';
import './GuidedTour.css';

/** Abstand zwischen hervorgehobenem Element und Erklärkarte */
const GAP = 16;
/** Mindestabstand der Karte zum Fensterrand */
const EDGE = 12;
/** Klebende Kopfzeile (64 px) plus etwas Luft */
const HEADER = 76;
/** Darunter steht die Karte als Leiste am unteren Rand statt frei schwebend */
const SHEET_WIDTH = 720;
/** So lange wird auf ein Element gewartet, das erst nach dem Seitenwechsel lädt */
const TARGET_TIMEOUT_MS = 6000;

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/**
 * Bringt das Ziel so ins Bild, dass die Karte danebenpasst.
 *
 * Zuerst die scrollbaren Behälter drumherum (die Menüleiste scrollt waagerecht,
 * die Seitenleiste senkrecht), danach das Fenster selbst. Von Hand statt per
 * `scrollIntoView`, weil die Kopfzeile klebt und sonst den oberen Rand des Ziels
 * verdeckt. Große Ziele docken oben an — mittig bliebe weder darüber noch
 * darunter Platz für die Karte.
 */
function scrollTargetIntoPlace(el: HTMLElement, sheetMode: boolean): void {
  el.scrollIntoView({ block: 'nearest', inline: 'center' });

  const box = el.getBoundingClientRect();
  const absoluteTop = window.scrollY + box.top;
  const viewport = window.innerHeight;
  const top =
    sheetMode || box.height > viewport * 0.45
      ? absoluteTop - HEADER
      : absoluteTop - (viewport - box.height) / 2;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

/**
 * Rahmen des Scheinwerfers: etwas Luft ums Element, aber nie über den
 * Fensterrand hinaus — sonst entstünde bei einem Element, das höher als das
 * Fenster ist, ein Ring ohne sichtbare Kante.
 */
function spotlightStyle(rect: DOMRect): React.CSSProperties {
  const top = Math.max(4, rect.top - 6);
  const left = Math.max(4, rect.left - 6);
  return {
    top,
    left,
    width: Math.min(rect.width + 12, window.innerWidth - left - 4),
    height: Math.min(rect.height + 12, window.innerHeight - top - 4),
  };
}

/**
 * Platz für die Karte suchen: rechts daneben, wenn das Ziel am linken Rand klebt
 * (Seitenleisten-Layout — so bleibt das ganze Menü sichtbar), sonst darunter,
 * sonst darüber. Am Ende immer in den sichtbaren Bereich geklemmt.
 */
function placeCard(rect: DOMRect, cw: number, ch: number): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (rect.right < vw * 0.4 && rect.right + GAP + cw <= vw - EDGE) {
    return {
      top: clamp(rect.top + rect.height / 2 - ch / 2, EDGE, Math.max(EDGE, vh - ch - EDGE)),
      left: rect.right + GAP,
    };
  }

  let top = rect.bottom + GAP;
  if (top + ch > vh - EDGE) {
    const above = rect.top - GAP - ch;
    top = above >= EDGE ? above : Math.max(EDGE, vh - ch - EDGE);
  }
  const left = clamp(
    rect.left + rect.width / 2 - cw / 2,
    EDGE,
    Math.max(EDGE, vw - cw - EDGE),
  );
  return { top, left };
}

/** Beobachtet eine Media Query und liefert, ob sie gerade zutrifft. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = () => setMatches(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

/**
 * Geführte Einführungstour durchs Callcenter.
 *
 * Läuft über alle Seiten: jeder Schritt kann die passende Seite öffnen und ein
 * echtes Element darauf hervorheben. Bereiche, die die Rolle nicht sehen darf,
 * fallen komplett aus der Tour. Startet beim allerersten Aufruf von allein und
 * lässt sich jederzeit über den Kompass in der Kopfzeile erneut aufrufen.
 */
export function GuidedTour() {
  const navigate = useNavigate();
  const location = useLocation();

  const userId = useAuthStore((s) => s.user?.id);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const hasRole = useAuthStore((s) => s.hasRole);
  const dialerStatus = useDialerStore((s) => s.status);

  const isRunning = useTourStore((s) => s.isRunning);
  const stepIndex = useTourStore((s) => s.stepIndex);
  const returnTo = useTourStore((s) => s.returnTo);
  // Beide werden nur abonniert, damit der Auto-Start unten erneut prüft, sobald
  // der gemerkte Stand aus der Datenbank eingetroffen ist.
  const isTourLoaded = useTourStore((s) => s.isLoaded);
  const seenVersion = useTourStore((s) => s.seenVersion);
  const initialize = useTourStore((s) => s.initialize);
  const shouldAutoStart = useTourStore((s) => s.shouldAutoStart);
  const start = useTourStore((s) => s.start);
  const next = useTourStore((s) => s.next);
  const back = useTourStore((s) => s.back);
  const goTo = useTourStore((s) => s.goTo);
  const finishTour = useTourStore((s) => s.finish);

  const setUserMenuOpen = useUiStore((s) => s.setUserMenuOpen);

  const isSheet = useMediaQuery(`(max-width: ${SHEET_WIDTH}px)`);
  const cardRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Rollen bestimmen, welche Bereiche es für diese Person überhaupt gibt —
  // dieselbe Logik wie im Menü, damit die Tour nie auf einen fehlenden
  // Menüpunkt zeigt.
  const canSeeTeam = hasRole(...PRIVILEGED_ROLES);
  const canSeeAdmin = hasRole('admin');
  const canSeeInsights = hasRole(...PRIVILEGED_ROLES, 'studioleiter');
  const steps = useMemo(
    () =>
      buildSteps({
        canSeeTeam,
        canSeeAdmin,
        canSeeInsights,
        canSeeScripts: canSeeTeam,
      }),
    [canSeeTeam, canSeeAdmin, canSeeInsights],
  );

  const total = steps.length;
  const index = Math.min(stepIndex, total - 1);
  const step: TourStep | undefined = total > 0 ? steps[index] : undefined;

  // Stand aus der Datenbank holen, sobald jemand angemeldet ist
  useEffect(() => {
    if (userId) void initialize(userId);
  }, [userId, initialize]);

  // Beim allerersten Besuch von allein starten — aber nicht mitten in einem
  // Gespräch und erst, wenn die Startseite wirklich steht.
  useEffect(() => {
    if (!isInitialized || !userId) return;
    if (dialerStatus !== 'idle') return;
    if (!shouldAutoStart()) return;
    const timer = window.setTimeout(() => {
      if (shouldAutoStart()) start(window.location.pathname);
    }, 900);
    return () => window.clearTimeout(timer);
  }, [
    isInitialized, userId, dialerStatus, shouldAutoStart, start,
    // isTourLoaded/seenVersion stehen hier, damit die Prüfung nachgeholt wird,
    // wenn der Stand erst nach dem ersten Rendern eintrifft.
    isTourLoaded, seenVersion,
  ]);

  const finish = useCallback(() => {
    const returnPath = returnTo;
    void finishTour();
    // Zurück auf die Seite, von der aus die Tour gestartet wurde — die Tour
    // hat den Nutzer quer durch die App geführt.
    if (returnPath && returnPath !== window.location.pathname) navigate(returnPath);
  }, [returnTo, finishTour, navigate]);

  // Konto-Menü aufziehen, solange ein Schritt seinen Inhalt erklärt, und sonst
  // wieder zuklappen. Läuft keine Tour, bleibt es zu — die Bedingung deckt
  // damit auch das Ende der Tour ab.
  useEffect(() => {
    setUserMenuOpen(isRunning && !!step?.openMenu);
  }, [isRunning, step, setUserMenuOpen]);

  // Seite des Schritts öffnen. Gemerkt wird der Schritt, nicht der Pfad —
  // manche Bereiche leiten intern weiter (/team → /team/calls) und würden
  // sonst in einer Dauerschleife neu angesteuert.
  const navigatedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isRunning || !step) {
      navigatedFor.current = null;
      return;
    }
    if (!step.route || navigatedFor.current === step.id) return;
    navigatedFor.current = step.id;
    if (location.pathname !== step.route) navigate(step.route);
  }, [isRunning, step, location.pathname, navigate]);

  // Hervorgehobenes Element suchen, ins Bild holen und nachführen.
  useEffect(() => {
    setRect(null);
    const selector = step?.target;
    if (!isRunning || !step || step.center || !selector) return;

    let cancelled = false;
    let raf = 0;
    let observer: ResizeObserver | null = null;
    let measure: (() => void) | null = null;
    const deadline = performance.now() + TARGET_TIMEOUT_MS;

    const track = (el: HTMLElement) => {
      scrollTargetIntoPlace(el, isSheet);
      measure = () => {
        if (!cancelled) setRect(el.getBoundingClientRect());
      };
      measure();

      // Während das sanfte Scrollen läuft, wandert das Element weiter. Statt
      // einmal nach fester Wartezeit nachzumessen, wird bis zur Ruhe
      // nachgeführt: höchstens eine Sekunde, Ende sobald sich nichts mehr tut.
      let stable = 0;
      let lastTop = Number.NaN;
      const followUntil = performance.now() + 1000;
      const follow = () => {
        if (cancelled) return;
        const top = el.getBoundingClientRect().top;
        stable = Math.abs(top - lastTop) < 0.5 ? stable + 1 : 0;
        lastTop = top;
        measure?.();
        if (stable < 4 && performance.now() < followUntil) {
          raf = requestAnimationFrame(follow);
        }
      };
      raf = requestAnimationFrame(follow);

      window.addEventListener('resize', measure);
      window.addEventListener('scroll', measure, true);
      observer = new ResizeObserver(measure);
      observer.observe(el);
    };

    // Seiten werden nachgeladen und holen erst danach ihre Daten — das Ziel
    // kann also ein paar hundert Millisekunden auf sich warten lassen.
    const seek = () => {
      if (cancelled) return;
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) {
        track(el);
        return;
      }
      // Kein Ziel gefunden: die Karte bleibt mittig stehen und erklärt den
      // Bereich trotzdem — besser als ein übersprungener Schritt.
      if (performance.now() < deadline) raf = requestAnimationFrame(seek);
    };
    raf = requestAnimationFrame(seek);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (measure) {
        window.removeEventListener('resize', measure);
        window.removeEventListener('scroll', measure, true);
      }
      observer?.disconnect();
    };
  }, [isRunning, step, isSheet]);

  // Karte neben das Element legen
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || !rect || isSheet) {
      setPos(null);
      return;
    }
    setPos(placeCard(rect, card.offsetWidth, card.offsetHeight));
  }, [rect, index, isSheet]);

  // Tastatur: → / Enter weiter, ← zurück, Esc beenden
  useEffect(() => {
    if (!isRunning) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); finish(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(total); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isRunning, total, next, back, finish]);

  if (!isRunning || !step) return null;

  const isLast = index >= total - 1;
  const isFirst = index === 0;
  const StepIcon = step.icon;
  const chapter = CHAPTERS.find((c) => c.id === step.chapter);
  const progress = ((index + 1) / total) * 100;

  // Kapitel-Leiste: alle Kapitel gleich breit — proportional zur Schrittzahl
  // wären die kurzen Kapitel so schmal, dass ihre Namen abgeschnitten werden.
  // Wie weit es insgesamt ist, zeigt der Balken oben an der Karte.
  const chapters = CHAPTERS.map((c) => {
    const own = steps.filter((s) => s.chapter === c.id);
    const doneCount = own.filter((s) => steps.indexOf(s) < index).length;
    return {
      ...c,
      count: own.length,
      firstIndex: steps.findIndex((s) => s.chapter === c.id),
      isCurrent: c.id === step.chapter,
      fill: own.length === 0 ? 0 : (c.id === step.chapter
        ? ((doneCount + 1) / own.length) * 100
        : doneCount === own.length ? 100 : 0),
    };
  }).filter((c) => c.count > 0);

  return (
    <div className="cctour" role="dialog" aria-modal="true" aria-labelledby="cctour-title">
      {rect ? (
        <div className="cctour__spotlight" style={spotlightStyle(rect)} />
      ) : (
        <div className="cctour__dim" />
      )}

      <div
        ref={cardRef}
        className={[
          'cctour__card',
          isSheet ? 'cctour__card--sheet' : '',
          !isSheet && !pos ? 'cctour__card--centered' : '',
        ].filter(Boolean).join(' ')}
        style={!isSheet && pos ? { top: pos.top, left: pos.left } : undefined}
      >
        <div className="cctour__progress" aria-hidden="true">
          <span className="cctour__progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <button className="cctour__close" onClick={finish} title="Tour beenden" aria-label="Tour beenden">
          <X size={16} />
        </button>

        <div className="cctour__head">
          <span className="cctour__icon"><StepIcon size={20} /></span>
          <div className="cctour__headtext">
            <span className="cctour__eyebrow">
              {chapter && (
                <span className="cctour__chip">
                  <chapter.icon size={11} />
                  {chapter.label}
                </span>
              )}
              <span className="cctour__count">Schritt {index + 1} von {total}</span>
            </span>
            <h2 className="cctour__title" id="cctour-title">{step.title}</h2>
          </div>
        </div>

        <div className="cctour__body">{step.body}</div>

        {step.tip && (
          <div className="cctour__tip">
            <Lightbulb size={14} />
            <span>{step.tip}</span>
          </div>
        )}

        <div className="cctour__rail" role="tablist" aria-label="Kapitel der Tour">
          {chapters.map((c) => (
            <button
              key={c.id}
              className={`cctour__rail-item${c.isCurrent ? ' is-current' : ''}`}
              onClick={() => goTo(c.firstIndex)}
              role="tab"
              aria-selected={c.isCurrent}
              title={`${c.label} — ${c.count} ${c.count === 1 ? 'Schritt' : 'Schritte'}`}
            >
              <span className="cctour__rail-track">
                <span className="cctour__rail-fill" style={{ width: `${c.fill}%` }} />
              </span>
              <span className="cctour__rail-label">{c.label}</span>
            </button>
          ))}
        </div>

        <div className="cctour__actions">
          {!isLast && (
            <button className="cctour__btn cctour__btn--ghost" onClick={finish}>
              Überspringen
            </button>
          )}
          <span className="cctour__keys" aria-hidden="true">
            <kbd>←</kbd><kbd>→</kbd>
          </span>
          <div className="cctour__actions-right">
            {!isFirst && (
              <button className="cctour__btn cctour__btn--secondary" onClick={back}>
                <ChevronLeft size={16} />
                Zurück
              </button>
            )}
            <button className="cctour__btn cctour__btn--primary" onClick={() => next(total)} autoFocus>
              {isLast ? 'Los geht’s' : 'Weiter'}
              {!isLast && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
